const KILO = 1000;
const GIGA = KILO * KILO * KILO;

export const MAX_FILE_SIZE = GIGA;

export const calculateFileSize = (size, locale) => {
  const kbSize = Math.ceil(size / KILO);
  const mbSize = kbSize / KILO;
  const useMb = mbSize >= 1;

  return useMb
    ? new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: 'megabyte',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(mbSize)
    : new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: 'kilobyte',
        maximumFractionDigits: 0,
      }).format(kbSize);
};

export const messageHasPendingFiles = message =>
  message.publicFileAttachments?.some(f => f.file?.attributes?.state === 'pendingVerification');
export const messageHasFailedFiles = message =>
  message.publicFileAttachments?.some(f => f.file?.attributes?.state === 'verificationFailed');

// Long enough for the browser to turn the iframe's response into a download.
const DOWNLOAD_IFRAME_LIFETIME_MS = 60 * 1000;

const isServedAsAttachment = url => {
  try {
    const disposition = new URL(url).searchParams.get('response-content-disposition') || '';
    return disposition
      .trim()
      .toLowerCase()
      .startsWith('attachment');
  } catch (e) {
    return false;
  }
};

/**
 * Downloads a file from a temporary download URL without leaving the page.
 *
 * Sharetribe's signed URLs ask S3 to serve the file as an attachment, so loading one in a hidden
 * iframe starts the download directly. Unlike window.open after an API call, this is not blocked as
 * a popup by mobile Safari; unlike navigating the page itself, it does not cancel in-flight requests
 * (uploads, verification polling) and does not trigger the unsent-uploads "leave page" guard.
 * A URL without an attachment disposition would render instead, so it opens in a new tab as before.
 *
 * @param {string} url - Temporary download URL from fileDownloads.create / ownFileDownloads.create
 */
export const startFileDownload = url => {
  if (!isServedAsAttachment(url)) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.tabIndex = -1;
  iframe.src = url;
  document.body.appendChild(iframe);
  window.setTimeout(() => iframe.remove(), DOWNLOAD_IFRAME_LIFETIME_MS);
};

export const analyseFileName = name => {
  const lastDot = name ? name.lastIndexOf('.') : -1;
  const baseName = lastDot > 0 ? name.slice(0, lastDot) : name;
  const extension = lastDot > 0 ? name.slice(lastDot) : '';
  return { baseName, extension };
};
